import {CleanHtml} from '@frontapp/editor-html';
import _ from 'lodash';
import React, {Component, ComponentProps} from 'react';

import {UnreachableError} from '../../../../libs/shared/core/src/helpers/types/customErrors';
import {AttachmentStatusesEnum} from '../../../../libs/shared/core/src/models/api/attachmentModel';
import {ChannelModel} from '../../../../libs/shared/core/src/models/api/channelModel';
import {
  DraftMessageModel,
  MessageStatusesEnum,
  MessageVisualTypesEnum,
  SendMessageOptionsModel,
} from '../../../../libs/shared/core/src/models/api/messageModel';
import {TeammateModel} from '../../../../libs/shared/core/src/models/api/teammateModel';
import {
  ConversationComposerWarningDialogTypesEnum,
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {TimelineVariationsEnum} from '../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {
  ComposerVariablesState,
  findApplicationObjectSlugsInHtml,
  findApplicationObjectTopics,
  findApplicationObjectUidFromSlugs,
  findFirstAppObjectSlugsInInterpolatedMessage,
  findTopicIdFromHtml,
} from '../../../helpers/models/applicationObjectVariableHelpers';
import {
  findSendWarnings,
  MessageWarning,
  MessageWarningTypesEnum,
} from '../../../helpers/models/messageHelpers';
import {ApplicationModel} from '../../../models/api/applicationModel';
import {ApplicationSettingsModel} from '../../../models/api/applicationSettingsModel';
import {ConversationModel} from '../../../models/api/conversationModel';
import {SearchTagModel} from '../../../models/api/searchTagModel';
import {TagModel} from '../../../models/api/tagModel';
import {TopicModel} from '../../../models/api/topicModel';
import {fetchMostRecentExternalShareFiles} from '../../../services/electron/electronBridgeOutbound';
import {VariableInterpolator} from '../../../services/variableInterpolation/variableInterpolator';
import {AttachmentFileUploader} from '../../common/attachments/attachmentsTypes';
import {RawVariable} from '../../common/editor/plugins/text/answerVariablePlugin';
import {TagTemplate} from '../../common/tags/helpers';
import {DraftMessageBannerContainer} from '../viewer/activity/message/banner/draftMessageBannerContainer';
import {SharedDraftMessageBannerContainer} from '../viewer/activity/message/banner/sharedDraftMessageBannerContainer';
import {MessageBase} from '../viewer/activity/message/messageBase';
import {ChatComposerPublicProps} from './chatComposer';
import {ChatComposerContainer} from './chatComposerContainer';
import {ComposerWarningsDialog} from './composerWarningsDialog';
import {EmailComposerContainer} from './emailComposerContainer';
import {ShareMessage} from './share/shareButtonCoordinator';
import {SendMessage} from './toolbar/buttons/sendToolbarButton';
import {TweetComposer, TweetComposerPublicProps} from './tweetComposer';

enum WarningClickActionsEnum {
  CANCEL = 'cancel',
  SEND = 'send',
}

/*
 * Props.
 */

type EmailComposerContainerProps = Omit<
  ComponentProps<typeof EmailComposerContainer>,
  'hasRawVariables' | 'onRawVariables' | 'onNewRawVariable' | 'hasOnlyApplicationRawVariables'
>;
type SpecificComposerProps = EmailComposerContainerProps & TweetComposerPublicProps & ChatComposerPublicProps;
type OmittedSpecificComposerProps = Omit<
  SpecificComposerProps,
  | 'attemptedSendDraftAttachments'
  | 'dismissAttemptedSendDraftAttachments'
  | 'onPaste'
  | 'isMarkdownEditor'
  | 'isFormattingToolbarOpen'
  | 'onIsFormattingToolbarOpenChange'
>;

export interface ComposerProps extends OmittedSpecificComposerProps {
  conversation: ConversationModel | null;
  channel: ChannelModel;
  message: DraftMessageModel;
  isMessageShared: boolean;
  interpolator: VariableInterpolator;
  createTag: (template: TagTemplate) => Promise<void>;
  tagMessage: (model: SearchTagModel) => void;
  untagMessage: (model: TagModel) => void;
  selectedTagIds: ReadonlyArray<number>;
  shouldWarnManyRecipients: boolean;
  shouldWarnMissingAttachments: boolean;
  shouldDynamicallyGrowComposer?: boolean;
  user: TeammateModel;
  author?: TeammateModel;
  /** Should force popout the composer as a child window. Provide this when popped out as a modal/dialog. */
  onForcePopout?: () => void;
  /** Should close the composer. Provide this when popped out as a modal/dialog. */
  onRequestClose?: () => void;
  /** Whether we're in a composer panel (not a viewer panel) and are popped out into a child window. */
  isComposerPopout: boolean;
  /** Whether we're in a composer panel (not a viewer panel) and are popped out into a modal/dialog. */
  isComposerPopup: boolean;
  /** Whether this is a standalone composer (/compose route). */
  isComposerStandalone: boolean;
  shareMessage: ShareMessage;
  onFileUpload: AttachmentFileUploader;
  shouldAttachExternalResources: boolean;
  timelineVariation: TimelineVariationsEnum;
  hasTwitterOauth2: boolean;
  popoutConversationTransitionViewer: () => void;
  topics: ReadonlyArray<TopicModel>;
  appSettings: ReadonlyArray<ApplicationSettingsModel>;
  applications: ReadonlyArray<ApplicationModel>;
  hasTopicsInMessageTemplates: boolean;
}

/*
 * Component.
 */

interface ComposerSendAttempt {
  sendOptions: Partial<SendMessageOptionsModel> | undefined;
  warnings: ReadonlyArray<MessageWarning>;
}

interface ComposerState {
  sendAttempt?: ComposerSendAttempt;
  attemptedSendDraftAttachments: boolean;
  rawVariables: ReadonlyArray<RawVariable>;
  variablesState?: ComposerVariablesState;
}

export class Composer extends Component<ComposerProps, ComposerState> {
  constructor(props: ComposerProps) {
    super(props);
    this.state = {
      attemptedSendDraftAttachments: false,
      rawVariables: [],
    };
  }

  /*
   * Event handlers.
   */

  private readonly onWarningsRequestClose = (
    {warnings}: ComposerSendAttempt,
    action: WarningClickActionsEnum,
  ) => {
    this.logWarningsDismiss(warnings, action);
    this.setState({sendAttempt: undefined});
  };

  private readonly onWarningsSendAnywayClick = (sendAttempt: ComposerSendAttempt) => {
    this.onWarningsRequestClose(sendAttempt, WarningClickActionsEnum.SEND);
    this.props.sendMessage(sendAttempt.sendOptions);
  };

  private readonly onRawVariables = (rawVariables: ReadonlyArray<RawVariable>) => {
    this.setState({rawVariables});
  };

  private readonly onNewRawVariable = (rawVariable: RawVariable) => {
    this.setState((state) => ({rawVariables: [...state.rawVariables, rawVariable]}));
  };

  private readonly dismissAttemptedSendDraftAttachments = () => {
    this.setState({attemptedSendDraftAttachments: false});
  };

  private readonly onSelectedTopicId = (selectedTopicId: number | undefined) => {
    this.setState((state) => {
      if (state.variablesState) {
        // If selected topic ID data is defined, the application object is also defined, so update topic ID.
        return {
          variablesState: {
            ...state.variablesState,
            topicId: selectedTopicId,
          },
        };
      }
      // Without an existing application object, the selected topic ID is always undefined.
      return {variablesState: undefined};
    });
  };

  private readonly onVariablesState = (variablesState: ComposerVariablesState | undefined) => {
    this.setState({variablesState});
  };

  private readonly sendMessage: SendMessage = (sendOptions) => {
    const {
      message,
      recipients,
      sendMessage,
      attachments,
      shouldWarnManyRecipients,
      shouldWarnMissingAttachments,
    } = this.props;

    // If the user attempts to send while attachments are uploading, setup a Composer banner by setting attemptedSendDraftAttachments and return.
    if (attachments.some((a) => a.status === AttachmentStatusesEnum.DRAFT && !a.hasFailed)) {
      this.setState({attemptedSendDraftAttachments: true});
      return;
    }

    const warnings = findSendWarnings(
      message,
      recipients,
      attachments,
      shouldWarnManyRecipients,
      shouldWarnMissingAttachments,
      this.state.rawVariables,
    );

    if (warnings.length > 0) {
      this.logWarningsDisplay(warnings);
      const sendAttempt: ComposerSendAttempt = {warnings, sendOptions};
      this.setState({sendAttempt});
      return;
    }

    sendMessage(sendOptions);
  };

  private maybeUpdateApplicationObject(previousProps: ComposerProps, isInitialLoad: boolean) {
    if (!this.props.hasTopicsInMessageTemplates) {
      return;
    }

    const {message} = this.props;
    const {body} = message;

    // Check if 1) the body has changed, 2) applications have loaded, 3) applications settings have loaded, or 4) topics have changed.
    const didApplicationsLoad = this.props.applications.length !== previousProps.applications.length;
    const didApplicationSettingsLoad = this.props.appSettings.length !== previousProps.appSettings.length;
    const didTopicsChange = this.props.topics.length !== previousProps.topics.length;
    const didBodyChange = this.props.message.body !== previousProps.message.body;
    if (
      !didApplicationsLoad &&
      !didApplicationSettingsLoad &&
      !didBodyChange &&
      !didTopicsChange &&
      !isInitialLoad
    ) {
      return;
    }

    // Recalculate application object.
    const applicationObjectSlugs = findFirstAppObjectSlugsInInterpolatedMessage(body);
    const objectUid = findApplicationObjectUidFromSlugs(applicationObjectSlugs, this.props.applications);

    // If the app object didn't change and topics also didn't change, early return.
    const didApplicationObjectChange = objectUid !== this.state.variablesState?.objectUid;
    if (!didApplicationObjectChange && !didTopicsChange) {
      return;
    }

    this.onApplicationObjectChange(body, objectUid);
  }

  private onApplicationObjectChange(body: CleanHtml, objectUid: string | undefined) {
    if (!objectUid) {
      this.onVariablesState(undefined);
      return;
    }

    // The topic ID in the HTML stores which topic is selected by the user.
    // The message may remain a draft, in which case we will know which topic was previously selected.
    const topicId = findTopicIdFromHtml(body);

    const applicationObjectTopics = findApplicationObjectTopics(this.props.topics, objectUid);
    const selectedTopic = computeSelectedTopic(topicId, applicationObjectTopics);

    if (selectedTopic) {
      this.onVariablesState({objectUid, topicId: selectedTopic.id});
    } else {
      this.selectFirstApplicationObjectTopic(applicationObjectTopics, objectUid);
    }
  }

  private selectFirstApplicationObjectTopic(
    applicationObjectTopics: ReadonlyArray<TopicModel>,
    objectUid: string,
  ) {
    // Auto-select the most recently updated topic.
    const firstTopic = _(applicationObjectTopics).sortBy('updatedAt').last();
    if (firstTopic) {
      this.onVariablesState({objectUid, topicId: firstTopic.id});
    } else {
      this.onVariablesState({objectUid, topicId: undefined});
    }
  }

  componentDidMount() {
    const attachExternalResourcesAsync = async () => {
      const shareFiles = await fetchMostRecentExternalShareFiles();
      if (shareFiles) {
        shareFiles.forEach((file) => this.props.onFileUpload(new File([file.data], file.name)));
      }
    };

    if (this.props.shouldAttachExternalResources) {
      attachExternalResourcesAsync();
    }

    this.maybeUpdateApplicationObject(this.props, true);
  }

  componentDidUpdate(previousProps: Readonly<ComposerProps>) {
    this.maybeUpdateApplicationObject(previousProps, false);
  }

  /*
   * Render.
   */

  render() {
    return (
      <MessageBase
        renderBanner={({isRounded}) => maybeRenderBanner(this.props, isRounded)}
        isPopout={this.props.isComposerPopout || this.props.isComposerStandalone}
        isPopup={this.props.isComposerPopup}
        timelineVariation={this.props.timelineVariation}
      >
        {this.renderComposer()}
        {this.maybeRenderWarnings()}
      </MessageBase>
    );
  }

  private readonly onEditorPaste: () => void = () => {
    trackInterfaceEvent(InterfaceEventsEnum.COMPOSER_COPIED_MESSAGE_INSERT, {
      is_reply: Boolean(this.props.message.inReplyToId),
      is_shared: this.props.isMessageShared,
      draft_id: this.props.message.id,
    });
  };

  private renderComposer() {
    const {attemptedSendDraftAttachments, rawVariables} = this.state;

    const rawApplicationVariables = rawVariables.filter((variable) => {
      const applicationObjectNames = findApplicationObjectSlugsInHtml(variable);
      return applicationObjectNames.length > 0;
    });

    const hasRawVariables = rawVariables.length > 0;
    const hasOnlyApplicationRawVariables =
      hasRawVariables && rawApplicationVariables.length === rawVariables.length;

    const composerProps = {
      ...this.props,
      key: this.props.message.uid,
      sendMessage: this.sendMessage,
      dismissAttemptedSendDraftAttachments: this.dismissAttemptedSendDraftAttachments,
      attemptedSendDraftAttachments,
      onPaste: this.onEditorPaste,
    };

    switch (this.props.message.visualType) {
      case MessageVisualTypesEnum.EMAIL:
        return (
          <EmailComposerContainer
            {...composerProps}
            hasRawVariables={hasRawVariables}
            hasOnlyApplicationRawVariables={hasOnlyApplicationRawVariables}
            onRawVariables={this.onRawVariables}
            onNewRawVariable={this.onNewRawVariable}
            variablesState={this.state.variablesState}
            onTopicIdSelect={this.onSelectedTopicId}
          />
        );

      case MessageVisualTypesEnum.TWEET:
        return <TweetComposer {...composerProps} />;

      case MessageVisualTypesEnum.CHAT:
        return <ChatComposerContainer {...composerProps} />;

      default:
        throw new Error('This should not happen.');
    }
  }

  private maybeRenderWarnings() {
    const {sendAttempt} = this.state;
    if (!sendAttempt || sendAttempt.warnings.length === 0) {
      return null;
    }

    const onRequestClose = () => this.onWarningsRequestClose(sendAttempt, WarningClickActionsEnum.CANCEL);

    const onOkClick = () => this.onWarningsSendAnywayClick(sendAttempt);

    return (
      <ComposerWarningsDialog
        warnings={sendAttempt.warnings}
        onRequestClose={onRequestClose}
        onOkClick={onOkClick}
      />
    );
  }

  private logWarningsDisplay(warnings: ReadonlyArray<MessageWarning>) {
    for (const warning of warnings) {
      trackInterfaceEvent(InterfaceEventsEnum.COMPOSER_MODAL_VIEW, this.buildBaseWarningLog(warning.type));
    }
  }

  private logWarningsDismiss(warnings: ReadonlyArray<MessageWarning>, action: WarningClickActionsEnum) {
    for (const warning of warnings) {
      trackInterfaceEvent(InterfaceEventsEnum.COMPOSER_MODAL_CLICK, {
        ...this.buildBaseWarningLog(warning.type),
        action,
      });
    }
  }

  private buildBaseWarningLog(warningType: MessageWarningTypesEnum) {
    return {
      namespace: this.props.channel.namespace,
      draft_id: this.props.message.id,
      conversation_id: this.props.conversation?.id || null,
      type: 'warning',
      object: computeLoggingWarningType(warningType),
    };
  }
}

function computeLoggingWarningType(
  warningType: MessageWarningTypesEnum,
): ConversationComposerWarningDialogTypesEnum {
  switch (warningType) {
    case MessageWarningTypesEnum.NO_SUBJECT:
      return ConversationComposerWarningDialogTypesEnum.NO_SUBJECT;

    case MessageWarningTypesEnum.NO_ATTACHMENTS:
      return ConversationComposerWarningDialogTypesEnum.NO_ATTACHMENTS;

    case MessageWarningTypesEnum.MANY_RECIPIENTS:
      return ConversationComposerWarningDialogTypesEnum.MANY_RECIPIENTS;

    case MessageWarningTypesEnum.FAILED_ATTACHMENTS:
      return ConversationComposerWarningDialogTypesEnum.FAILED_ATTACHMENTS;

    case MessageWarningTypesEnum.UPLOADING_ATTACHMENTS:
      return ConversationComposerWarningDialogTypesEnum.UPLOADING_ATTACHMENTS;

    case MessageWarningTypesEnum.HAS_RAW_VARIABLES:
      return ConversationComposerWarningDialogTypesEnum.RAW_VARIABLES;

    default:
      throw new UnreachableError(warningType);
  }
}

function maybeRenderBanner(props: ComposerProps, isRounded: boolean) {
  const {message, author, shareMessage, isMessageShared} = props;

  // The message is shared, let's display a banner to remind the user.
  if (author && isMessageShared && message.status === MessageStatusesEnum.DRAFT_COMPOSE) {
    return (
      <SharedDraftMessageBannerContainer
        author={author}
        message={message}
        panelId={props.panelId}
        onForcePopout={props.onForcePopout}
        onRequestClose={props.onRequestClose}
        isComposerPopup={props.isComposerPopup}
        isRounded={isRounded}
      />
    );
  }

  if (message.status === MessageStatusesEnum.DRAFT_COMPOSE) {
    // The message is a draft, let's display a banner to remind the user.
    return (
      <DraftMessageBannerContainer
        message={message}
        panelId={props.panelId}
        shareMessage={shareMessage}
        onForcePopout={props.onForcePopout}
        onRequestClose={props.onRequestClose}
        isComposerPopup={props.isComposerPopup}
        isRounded={isRounded}
      />
    );
  }

  return null;
}

function computeSelectedTopic(
  topicId: string | undefined,
  applicationObjectTopics: ReadonlyArray<TopicModel>,
): TopicModel | undefined {
  if (!topicId || topicId === 'undefined') {
    return undefined;
  }
  const integerTopicId = parseInt(topicId, 10);
  return applicationObjectTopics.find((topic) => topic.id === integerTopicId);
}
